import { _js } from '@ifixit/utils/localize';
import { createContext, Dispatch, SetStateAction, useContext, useState } from 'react';
import type { ComponentType } from 'react';
import { requireAuth } from 'Shared/require-auth';
import LoadingIndicator from 'Shared/loading_indicator';
import ApiRequest from 'Shared/api_2_0';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { GuideSubscriptionContext } from './GuideSubscription';

const noop = () => {};

const defaultContext = {
   isFavorite: false,
   favorite: noop as (_guideid: number, _langid: string, _isTeardown: boolean) => void,
   unfavorite: noop as (_guideid: number, _langid: string, _isTeardown: boolean) => void,
   favoriteCount: 0,
};

const GuideFavoriteContext = createContext(defaultContext);

function withGuideFavorite<T>(Component: ComponentType<T>) {
   return function GuideFavoriteProvider(
      props: { isFavorite?: boolean; favoriteCount?: number } & T
   ) {
      const [isFavorite, setIsFavorite] = useState(props.isFavorite || defaultContext.isFavorite);
      const [favoriteCount, setFavoriteCount] = useState(
         props.favoriteCount || defaultContext.favoriteCount
      );

      const guideSubscriptionContext = useContext(GuideSubscriptionContext);

      const guideFavoriteContext = {
         favorite: (guideid: number, langid: string, isTeardown: boolean) => {
            doFavoriteGuide(
               guideid,
               langid,
               isTeardown,
               true,
               setIsFavorite,
               setFavoriteCount,
               favoriteCount
            );
            guideSubscriptionContext.subscribe(guideid, langid);
         },
         unfavorite: (guideid: number, langid: string, isTeardown: boolean) => {
            doFavoriteGuide(
               guideid,
               langid,
               isTeardown,
               false,
               setIsFavorite,
               setFavoriteCount,
               favoriteCount
            );
            guideSubscriptionContext.unsubscribe(guideid, langid);
         },
         isFavorite,
         favoriteCount,
      };

      return (
         <GuideFavoriteContext.Provider value={guideFavoriteContext}>
            <Component {...props} />
         </GuideFavoriteContext.Provider>
      );
   };
}

function doFavoriteGuide(
   guideid: number,
   langid: string,
   isTeardown: boolean,
   wantsToLike: boolean,
   setLiked: Dispatch<SetStateAction<boolean>>,
   setLikedCount: Dispatch<SetStateAction<number>>,
   favoriteCount: number
) {
   requireAuth({
      message: _js('Log in to "favorite" this.'),
      onAuthorize: LoadingIndicator.withEventHandler(
         () => {
            const url = `user/favorites/guides/${guideid}`;
            const method = wantsToLike ? 'PUT' : 'DELETE';
            const request = new ApiRequest(url, { method });
            return request.send({ langid: langid }).then(() => {
               const type = isTeardown ? 'Teardown' : 'Guide';
               const verb = wantsToLike ? 'Save to' : 'Remove from';
               trackAnalyticsCustomEvent({
                  eventCategory: `${type} Options`,
                  eventAction: `${type} Options - ${verb} Favorites`,
               });
               setLiked(wantsToLike);
               setLikedCount(wantsToLike ? favoriteCount + 1 : favoriteCount - 1);
               // Update legacy like count element (used in teardowns)
               const countElements = document.querySelectorAll('.js-like-count');
               if (countElements.length) {
                  countElements.forEach(
                     element =>
                        (element.textContent = `${
                           wantsToLike ? favoriteCount + 1 : favoriteCount - 1
                        }`)
                  );
               }
            });
         },
         {
            pendingMessage: wantsToLike ? _js('Adding Favorite') : _js('Removing Favorite'),
            successMessage: wantsToLike ? _js('Added Favorite') : _js('Removed Favorite'),
            failureMessage: _js('Failure'),
         }
      ),
   });
}

export { GuideFavoriteContext, withGuideFavorite };
